import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { queryParamsModule } from '@/modules/queryParams';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useOnboarding } from '@/pages/Onboarding/context';
import { LONELINESS_OPTIONS } from '@/pages/Onboarding/options/Loneliness/LONELINESS_OPTIONS';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataKeyType, LayoutDataType } from '../../types';

const ScreenLonelinessTitleStyles = styled(OnboardingTitle)`
  .ion-page & {
    margin: 20px auto;
  }
`;

export const ScreenLoneliness: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Loneliness',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const { refreshUser } = useAccount();
  const { watch, getValues } = useFormContext<LayoutDataType>();
  const loneliness = watch('loneliness');

  const handleNextStep = (): void => {
    const lonelinessOption = LONELINESS_OPTIONS.find(
      (option) => option.id === loneliness,
    );

    const { name } = getValues();

    userService
      .createOrUpdate({
        name,
        onboarding_data: {
          loneliness: lonelinessOption?.title ?? '',
        },
      })
      .then(() => {
        refreshUser();
      });

    wizard.nextStep();
  };

  const handleBackStep = () => {
    wizard.goToStep('name');
  };

  return (
    <OnboardingPage>
      <OnboardingNavigation onBack={handleBackStep} />

      <ScreenLonelinessTitleStyles>
        Reflecting on loneliness: do you ever feel it?
      </ScreenLonelinessTitleStyles>

      <IonContent>
        <OnboardingOptionsList>
          {LONELINESS_OPTIONS.map((option) => (
            <OnboardingOptionItem
              checked={loneliness === option.id}
              control={form.control}
              option={option}
              name="loneliness"
              key={option.id}
              onChange={(value) => {
                queryParamsModule.setParam<LayoutDataKeyType>(
                  'loneliness',
                  value,
                );
              }}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>

      <OnboardingFooter disabled={!loneliness} onContinue={handleNextStep} />
    </OnboardingPage>
  );
};
